import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  onPageChange?: (page: number) => void;
  onNext?: () => void;
  onPrev?: () => void;
  // Also accept usePagination hook's naming
  goToPage?: (page: number) => void;
  nextPage?: () => void;
  prevPage?: () => void;
  // Allow extra props from spread without error
  paginatedItems?: any[];
  resetPage?: () => void;
}
export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  onPageChange,
  onNext,
  onPrev,
  goToPage,
  nextPage,
  prevPage
}: PaginationProps) {
  const handlePageChange = onPageChange || goToPage || (() => {});
  const handleNext = onNext || nextPage || (() => {});
  const handlePrev = onPrev || prevPage || (() => {});
  if (totalPages <= 1) return null;
  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const maxVisible = isMobile ? 3 : 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };
  return <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4" data-id="element-4623">
      <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block" data-id="element-4624">
        Mostrando{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4625">
          {startIndex + 1}
        </span>{' '}
        a{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4626">
          {endIndex}
        </span>{' '}
        de{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4627">
          {totalItems}
        </span>{' '}
        registros
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 sm:hidden" data-id="element-4628">
        {startIndex + 1}-{endIndex} de {totalItems}
      </p>

      <div className="flex items-center gap-1" data-id="element-4629">
        <button onClick={handlePrev} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Página anterior" data-id="element-4630">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-4631" />
        </button>

        {getPageNumbers().map((page, idx) => typeof page === 'string' ? <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm" data-id="element-4632">
              …
            </span> : <button key={page} onClick={() => handlePageChange(page)} className={`min-w-[32px] sm:min-w-[36px] h-8 sm:h-9 rounded-lg text-sm font-medium transition-colors ${page === currentPage ? 'bg-sky-500 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-4633">
              {page}
            </button>)}

        <button onClick={handleNext} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Página siguiente" data-id="element-4634">
          <ChevronRightIcon className="w-4 h-4" data-id="element-4635" />
        </button>
      </div>
    </div>;
}